import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMemo, useState } from 'react';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useProvinceContext } from '../../../contexts/ProvinceProvider';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { addEditProvinceSchema } from '../../../schemas';
import { SelectData } from '../../../types/common';
import { ProvinceReqBody } from '../../../types/province';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import SelectedProvincesTable from '../../SetupSubPageComps/ProvincesComps/SelectedProvincesTable';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCountrySelect from '../Inputs/AcademiaCountrySelect';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaModalLoading from '../Loadings/AcademiaModalLoading';
import NoTableDataFound from '../NoTableDataFound';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AddProvinceModal = ({
  handleModalClose,
}: {
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [selectedCountry, setSelectedCountry] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  const [resetProvinceValue, setResetProvinceValue] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // integration of custom API hooks here
  const { getCountryWiseProvincesHandler, addNewProvinceHandler } =
    useProvincesActions('', selectedCountry.name);

  // integration of context hooks here
  const { provinces, selectedProvinces, setSelectedProvinces } =
    useProvinceContext();
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  console.log(
    'COUNTRY WISE PROVINCES: ',
    getCountryWiseProvincesHandler.data?.data
  );

  const provinceOptions = useMemo(() => {
    if (!getCountryWiseProvincesHandler.data?.data) return [];

    return getCountryWiseProvincesHandler.data.data
      .filter(
        (province) =>
          !provinces.some(
            (addedProvince) =>
              addedProvince.province_name === province.province_name
          )
      )
      .filter(
        (province) =>
          !selectedProvinces.some(
            (addedProvince) =>
              addedProvince.province_name === province.province_name
          )
      )
      .map((province, index) => ({
        id: index,
        name: province.province_name,
        code: province.province_code,
      }));
  }, [getCountryWiseProvincesHandler.data?.data, selectedProvinces, provinces]);

  // handler function to handle add/edit province declared here
  const addEditProvinceHandler = async () => {
    try {
      setIsSubmitting(true);

      const body = selectedProvinces.map((province) => ({
        country: province.country,
        province_code: province.province_code,
        province_name: province.province_name,
        gst: province.gst,
        hst: province.hst,
        pst: province.pst,
        legal_age: province.legal_age,
        created_by: currentUser?.username,
      }));

      await addNewProvinceHandler
        .mutateAsync({
          body,
        })
        .then((res) => {
          if (res.is_success) {
            toast.success('New Province Added!!');

            setSelectedProvinces([]);
            handleModalClose();
            queryClient.invalidateQueries({ queryKey: ['province-list'] });
          } else {
            toast.error(res.message || 'Province Adding Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addProvinceToSelectedList = (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    const selectedProvince: ProvinceReqBody = {
      country_name: selectedCountry.name,
      country: selectedCountry.id,
      province_code: values.province.code,
      province_name: values.province.name,
      iso_code: selectedCountry.code,
      gst: values.gst,
      hst: values.hst,
      pst: values.pst,
      legal_age: values.legal_age,
      created_by: currentUser?.username,
    };

    setSelectedProvinces((state) => [...state, selectedProvince]);

    setResetProvinceValue(true);
    actions.setSubmitting(false);
    actions.resetForm();
  };

  // rendering add/edit province modal component here
  return (
    <ParentModal
      onClick={() => {
        handleModalClose();
        setSelectedProvinces([]);
      }}
      customWidthClass='md:w-[500px] lg:w-[95%] xl:w-[1200px]'
    >
      <ModalTitle titleText='add province' />

      <div className='grid lg:grid-cols-[280px_1fr] xl:grid-cols-[400px_1fr] gap-5'>
        <Formik
          initialValues={{
            country: SELECT_FIELD_DEFAULT_VALUE,
            province: SELECT_FIELD_DEFAULT_VALUE,
            legal_age: '',
            gst: '',
            hst: '',
            pst: '',
          }}
          validationSchema={addEditProvinceSchema}
          onSubmit={addProvinceToSelectedList}
        >
          {(props) => (
            <Form className='space-y-4'>
              <AcademiaCountrySelect
                name='country'
                label='Select Country'
                setCountry={setSelectedCountry}
                isRequired
              />

              {getCountryWiseProvincesHandler.isLoading ? (
                <AcademiaModalLoading />
              ) : getCountryWiseProvincesHandler.isSuccess ? (
                <>
                  <AcademiaFormSelect
                    name='province'
                    label='Select Province'
                    options={provinceOptions}
                    defaultValue={props.values.province}
                    resetSelectField={resetProvinceValue}
                    setResetSelectField={() => setResetProvinceValue(false)}
                    showSearchInput
                    isRequired
                  />

                  <AcademiaInputField
                    type='number'
                    name='legal_age'
                    label='Legal Age'
                    placeholder='Enter legal age of the province here...'
                    isRequired
                  />

                  <AcademiaInputField
                    type='number'
                    name='gst'
                    label='Goods & Services Tax'
                    placeholder='Enter GST rate here... (E.g. 10)'
                    isRequired
                  />

                  <AcademiaInputField
                    type='number'
                    name='hst'
                    label='Harmonized Sales Tax'
                    placeholder='Enter HST rate here... (E.g. 10)'
                    isRequired
                  />

                  <AcademiaInputField
                    type='number'
                    name='pst'
                    label='Provincial Sales Tax'
                    placeholder='Enter PST rate here... (E.g. 10)'
                    isRequired
                  />
                </>
              ) : getCountryWiseProvincesHandler.isError ? (
                <NoTableDataFound message='Failed to load provinces!!' />
              ) : null}

              <AcademiaButton
                title={
                  props.values.province.id === -1
                    ? 'Please, select a province first!'
                    : ''
                }
                type='submit'
                variant='success'
                additionalClassNames='!py-2'
                icon={<TbSquareRoundedPlusFilled className='size-6' />}
                isLoading={props.isSubmitting}
                isDisabled={props.values.province.id === -1}
                fullWidth
              >
                Add province
              </AcademiaButton>
            </Form>
          )}
        </Formik>

        <SelectedProvincesTable />
      </div>

      <div className='w-full md:w-3/5 mx-auto !mt-5'>
        <AcademiaButton
          title={
            !selectedProvinces.length ? 'Please, add some province first!' : ''
          }
          type='button'
          onClick={addEditProvinceHandler}
          variant='premium'
          icon={<TbSquareRoundedPlusFilled className='size-6' />}
          isLoading={isSubmitting}
          isDisabled={!selectedProvinces.length}
          fullWidth
        >
          Submit province
        </AcademiaButton>
      </div>
    </ParentModal>
  );
};

export default AddProvinceModal;
